import { type SavedSearch } from 'bundles/connect/types';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import EmptyState from '../empty_state';
import { faHouseBuilding, faList, faSign } from '@fortawesome/pro-solid-svg-icons';
import { Col, Nav, Row, Tab } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SavedSearchEditModal from './saved_search_edit_modal';
import SavedSearchCard from './saved_search_card';
import SavedSearchDeleteModal from './saved_search_delete_modal';

type Props = {
  canViewExclusiveListings: boolean;
  initialSavedSearches: SavedSearch[];
}

export default function SavedSearches({ canViewExclusiveListings, initialSavedSearches }: Props) {
  const [savedSearchToEdit, setSavedSearchToEdit] = useState<SavedSearch | null>(null);
  const [savedSearchToDelete, setSavedSearchToDelete] = useState<SavedSearch | null>(null);
  const [savedSearches, setSavedSearches] = useState(initialSavedSearches);
  const handleEdit = (savedSearch) => {
    setSavedSearchToEdit(savedSearch);
  };

  const handleUpdate = (updatedSearch) => {
    setSavedSearches((prevSearches) => {
      return prevSearches.map(search => search.id === updatedSearch.id ? updatedSearch : search);
    });
  };

  const handleDelete = (id) => {
    setSavedSearches(prevItems => prevItems.filter(item => item.id !== id));
  };

  if (isEmpty(savedSearches)) {
    return (
      <div className="d-flex justify-content-center align-items-center p-6 w-100">
        <EmptyState icon={faList}
          iconSize="2x"
          text="Go to the search page, add filters, and save"
          title="You don't have any saved searches"
        />
      </div>
    );
  }

  return (
    <div>
      <Tab.Container defaultActiveKey="all">
        {canViewExclusiveListings && (
          <Row>
            <Col>
              <Nav className="bg-white border-bottom" variant="tabs">
                <Nav.Item>
                  <Nav.Link className="d-flex align-items-center gap-2" eventKey="all">
                    <FontAwesomeIcon icon={faHouseBuilding} />
                    All Properties
                  </Nav.Link>
                </Nav.Item>

                
                <Nav.Item>
                  <Nav.Link
                    className="d-flex align-items-center gap-2"
                    eventKey="exclusive"
                  >
                    <FontAwesomeIcon icon={faSign} />
                    Exclusive Listings
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
          </Row>
        )}

        <Row className="p-6">
          <Col>
            <Tab.Content>
              <Tab.Pane eventKey="all">
                <div>
                  {savedSearches.filter(s => s.offMarketSearch).length > 0 ? (
                    <Row className="g-4">
                      {savedSearches
                        .filter(s => s.offMarketSearch)
                        .map(s => (
                          <Col key={s.id} lg={4} md={6} xs={12}>
                            <SavedSearchCard
                              savedSearch={s}
                              onDelete={() => setSavedSearchToDelete(s)}
                              onEdit={handleEdit}
                              onUpdate={handleUpdate}
                            />
                          </Col>
                        ))}
                    </Row>
                  ) : (
                    <EmptyState
                      icon={faList}
                      iconSize="2x"
                      text="Go to the search page, add filters, and save"
                      title="You don't have any saved searches"
                    />
                  )}
                </div>
              </Tab.Pane>

              {canViewExclusiveListings && (
                <Tab.Pane eventKey="exclusive">
                  <div>
                    {savedSearches.filter(s => !s.offMarketSearch).length > 0 ? (
                      <Row className="g-4">
                        {savedSearches
                          .filter(s => !s.offMarketSearch)
                          .map(s => (
                            <Col key={s.id} md={4} xs={6}>
                              <SavedSearchCard
                                savedSearch={s}
                                onDelete={() => setSavedSearchToDelete(s)}
                                onEdit={handleEdit}
                                onUpdate={handleUpdate}
                              />
                            </Col>
                          ))}
                      </Row>
                    ) : (
                      <EmptyState
                        icon={faList}
                        iconSize="2x"
                        text="Go to the search page, add filters, and save"
                        title="You don't have any exclusive listing saved searches"
                      />
                    )}
                  </div>
                </Tab.Pane>
              )}
            </Tab.Content>
          </Col>
        </Row>

      </Tab.Container>
      <SavedSearchEditModal
        savedSearch={savedSearchToEdit}
        onHide={() => setSavedSearchToEdit(null)}
        onUpdate={handleUpdate}
      />
      <SavedSearchDeleteModal
        savedSearch={savedSearchToDelete}
        onDelete={handleDelete}
        onHide={() => setSavedSearchToDelete(null)}
      />
    </div>
  );
}
